import { z } from "zod";

const identifierSchema = z.string().trim().min(1).max(200);
const nonEmptyTextSchema = z.string().trim().min(1);
const artifactHashSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);

export const revisionIntentSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("edit_text"),
      slideId: identifierSchema,
      componentId: identifierSchema,
      text: nonEmptyTextSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("edit_notes"),
      slideId: identifierSchema,
      speakerNotes: z.string(),
    })
    .strict(),
  z
    .object({
      type: z.literal("set_lock"),
      slideId: identifierSchema,
      componentId: identifierSchema.optional(),
      locked: z.boolean(),
    })
    .strict(),
]);

const revisionMutationInputSchema = z
  .object({
    projectId: identifierSchema,
    commandId: identifierSchema,
    expectedDeckHash: artifactHashSchema,
    intent: revisionIntentSchema,
  })
  .strict();

export const revisionCommandSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("inspect_revision"),
      projectId: identifierSchema,
    })
    .strict(),
  z
    .object({
      type: z.enum(["preview_revision", "apply_revision"]),
      input: revisionMutationInputSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("compare_revision_versions"),
      projectId: identifierSchema,
      leftVersionId: identifierSchema,
      rightVersionId: identifierSchema,
    })
    .strict(),
]);

const revisionSlideSchema = z
  .object({
    slideId: identifierSchema,
    order: z.number().int().positive(),
    title: nonEmptyTextSchema,
    titleComponentId: identifierSchema.optional(),
    locked: z.boolean(),
    lockedComponentIds: z.array(identifierSchema),
  })
  .strict();

export const revisionSnapshotSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    projectId: identifierSchema,
    deckHash: artifactHashSchema,
    currentVersionId: identifierSchema,
    versionCount: z.number().int().positive(),
    slides: z.array(revisionSlideSchema).min(1),
    recentEvent: nonEmptyTextSchema,
  })
  .strict();

export const revisionImpactSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    projectId: identifierSchema,
    commandId: identifierSchema,
    expectedDeckHash: artifactHashSchema,
    affectedSlideIds: z.array(identifierSchema),
    unaffectedSlideIds: z.array(identifierSchema),
    invalidatedNodeIds: z.array(identifierSchema),
    invalidatedArtifactKinds: z.array(
      z.enum(["deck_spec", "delivery_manifest", "evaluation_report"]),
    ),
    requiresConfirmation: z.literal(true),
    nextActions: z.array(nonEmptyTextSchema).min(1),
  })
  .strict();

export const revisionComparisonSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    projectId: identifierSchema,
    leftVersionId: identifierSchema,
    rightVersionId: identifierSchema,
    changedSlideIds: z.array(identifierSchema),
    unchangedSlideIds: z.array(identifierSchema),
  })
  .strict();

export const revisionCommandResultSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("snapshot"), value: revisionSnapshotSchema }).strict(),
  z.object({ kind: z.literal("impact"), value: revisionImpactSchema }).strict(),
  z
    .object({ kind: z.literal("comparison"), value: revisionComparisonSchema })
    .strict(),
]);

export type RevisionIntent = z.infer<typeof revisionIntentSchema>;
export type RevisionCommand = z.infer<typeof revisionCommandSchema>;
export type RevisionSnapshot = z.infer<typeof revisionSnapshotSchema>;
export type RevisionImpact = z.infer<typeof revisionImpactSchema>;
export type RevisionComparison = z.infer<typeof revisionComparisonSchema>;
export type RevisionCommandResult = z.infer<typeof revisionCommandResultSchema>;

export interface RevisionCommandPort {
  executeRevision(command: RevisionCommand): Promise<RevisionCommandResult>;
}
